import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Firm, FirmType, InvoiceType } from "../types";
import { firmService, invoiceTypeService } from "../services/dataService";
import { turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import "./FirmPicker.css";

/**
 * Firma seçici — tüm firma seçim alanlarının tek bileşeni. İki modal, tek gövde:
 *  - Tekli: `FirmPickerField` + `FirmPickerModal` — seçince kapanır, son seçilenleri tutar.
 *  - Çoklu: `FirmMultiPickerField` + `FirmMultiPickerModal` — işaretlemeler taslakta birikir,
 *    sayfaya yalnız "Uygula" (Ctrl+Enter) ile iner; Esc / Vazgeç / overlay taslağı atar.
 *  - `PickerBase` (dışa açık değil): arama (ad, kod, vergi no, yetkili, telefon, e-posta, şehir),
 *    tür ve pasif süzgeçleri, klavye gezinimi, satır içi hızlı firma oluşturma.
 *  - `firmDirectory` / `useFirmDirectory`: firma listesi oturum boyunca tek yerden, 5 dk önbellekli.
 * Portal ile body'ye açılır; iç modallerin (ör. kasa hareketi, sipariş düzenleme) üstünde durur ve
 * tıklama/klavye olayları üst overlay'lere sızmaz.
 */

// ───────────────────────────── Firma dizini ─────────────────────────────

const TTL = 5 * 60 * 1000;
let cache: { at: number; list: Firm[] } | null = null;
let inflight: Promise<Firm[]> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const firmDirectory = {
  load(force = false): Promise<Firm[]> {
    if (!force && cache && Date.now() - cache.at < TTL) return Promise.resolve(cache.list);
    if (inflight) return inflight;
    inflight = firmService.getFirms()
      .then((list) => { cache = { at: Date.now(), list }; notify(); return list; })
      .finally(() => { inflight = null; });
    return inflight;
  },
  peek(): Firm[] | null { return cache?.list ?? null; },
  invalidate() { cache = null; notify(); },
  upsert(f: Firm) { if (cache) { cache.list = [f, ...cache.list.filter((x) => x.firmId !== f.firmId)]; notify(); } },
};

export function useFirmDirectory(enabled = true) {
  const [firms, setFirms] = useState<Firm[]>(() => cache?.list ?? []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reload = useCallback(async (force = false) => {
    setLoading(true); setError("");
    try { setFirms(await firmDirectory.load(force)); }
    catch (err: any) { setError(err?.response?.status === 403 ? "Firma listesine erişim izniniz yok." : "Firmalar yüklenemedi."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    if (!enabled) return;
    const l = () => { const c = firmDirectory.peek(); if (c) setFirms(c); };
    listeners.add(l);
    reload();
    return () => { listeners.delete(l); };
  }, [enabled, reload]);
  return { firms, loading, error, reload };
}

// ───────────────────────────── Yardımcılar ─────────────────────────────

const RECENT_MAX = 6;
const recentKey = () => {
  try { const u = JSON.parse(localStorage.getItem("user") || "null"); return `nedaris-firm-recent:${u?.userId || "anon"}`; } catch { return "nedaris-firm-recent:anon"; }
};
const readRecent = (): string[] => { try { return JSON.parse(localStorage.getItem(recentKey()) || "[]"); } catch { return []; } };
const pushRecent = (id: string) => { try { localStorage.setItem(recentKey(), JSON.stringify([id, ...readRecent().filter((x) => x !== id)].slice(0, RECENT_MAX))); } catch { /* özel pencere */ } };

export const firmInitials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
export const firmHue = (s: string) => { let h = 0; for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 360; return h; };
const typeLabel = (f: Firm) => (f.firmType === FirmType.GercekKisi ? "Şahıs" : "Tüzel");
const taxText = (f: Firm) => (f.firmType === FirmType.GercekKisi ? (f.tckn ? `TC ${f.tckn}` : "") : (f.taxNumber ? `VKN ${f.taxNumber}` : ""));
const subText = (f: Firm) => [typeLabel(f), f.city?.name, taxText(f)].filter(Boolean).join(" · ");
const fieldSub = (f: Firm) => [f.firmCode, f.contactPerson || f.city?.name].filter(Boolean).join(" · ") || typeLabel(f);
const sortTr = (a: Firm, b: Firm) => a.firmName.localeCompare(b.firmName, "tr");
const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((id) => b.includes(id));
const matches = (f: Firm, q: string) =>
  !q || [f.firmName, f.firmCode, f.taxNumber, f.tckn, f.contactPerson, f.phone, f.email, f.city?.name, f.firstName && f.lastName ? `${f.firstName} ${f.lastName}` : ""]
    .some((s) => s && turkishIncludes(String(s), q));

/** Firma adından benzersiz kod: ilk 3 harf + sıra (eski FirmModal ile aynı kural). */
const generateFirmCode = (firmName: string, existing: Firm[]) => {
  const clean = firmName.toUpperCase().replace(/Ğ/g, "G").replace(/Ü/g, "U").replace(/Ş/g, "S").replace(/İ/g, "I").replace(/Ö/g, "O").replace(/Ç/g, "C").replace(/[^A-Z0-9]/g, "");
  const prefix = clean.substring(0, 3).padEnd(3, "X");
  const codes = new Set(existing.filter((f) => f.firmCode?.startsWith(prefix)).map((f) => f.firmCode));
  let n = 1; let code = `${prefix}${String(n).padStart(3, "0")}`;
  while (codes.has(code)) { n++; code = `${prefix}${String(n).padStart(3, "0")}`; }
  return code;
};

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V7l6-4 6 4v14M15 21V11l6 3v7M3 21h18M7 9h2M7 13h2M7 17h2M11 9h2M11 13h2M11 17h2" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  phone: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>,
};

export const FirmMark: React.FC<{ firm: Firm; big?: boolean }> = ({ firm, big }) => (
  <span className={`fp-mark ${big ? "big" : ""} ${firm.isActive === false ? "off" : ""}`} style={{ ["--fp-hue" as any]: firmHue(firm.firmCode || firm.firmName) }}>{firmInitials(firm.firmName)}</span>
);

// ───────────────────────────── Ortak modal gövdesi ─────────────────────────────

type PickerMode = "single" | "multi";

interface PickerBaseProps {
  mode: PickerMode;
  open: boolean;
  onClose: () => void;
  /** Dolu halka gösterilecek satırlar. Tekli: 0–1 kimlik; çoklu: taslak. */
  selectedIds: ReadonlySet<string>;
  /** Satır / üst satır / hızlı oluşturma. Tekli sarmalayıcı seçip kapatır; çoklu taslakta aç/kapa (null = taslağı boşalt). */
  onPick: (firm: Firm | null) => void;
  /** Çoklu: "Uygula" ve Ctrl+Enter. */
  onCommit?: () => void;
  /** Çoklu: "Görünenleri seç / kaldır". */
  onPickMany?: (firms: Firm[], add: boolean) => void;
  /** Çoklu: "Seçili" grubunda gösterilecek kimlikler (açılıştaki anlık görüntü + bu oturumda işaretlenenler). */
  pinnedIds?: readonly string[];
  firms?: Firm[];
  title: string;
  nullLabel?: string;
  allowCreate?: boolean;
  allowClear?: boolean;
}

type Row = { kind: "head"; key: string; label: string; icon?: React.ReactNode } | { kind: "firm"; key: string; firm: Firm; recent?: boolean };
type TypeChip = "" | "tuzel" | "sahis";

const emptyCreate = () => ({ firmName: "", firmType: FirmType.TuzelKisi as FirmType, contactPerson: "", phone: "", email: "", taxNumber: "", invoiceTypeId: "", address: "" });
let seq = 0;

const PickerBase: React.FC<PickerBaseProps> = ({ mode, open, onClose, selectedIds, onPick, onCommit, onPickMany, pinnedIds, firms: given, title, nullLabel, allowCreate = true, allowClear = true }) => {
  const multi = mode === "multi";
  const { has } = usePermissions();
  const dir = useFirmDirectory(open && !given);
  const source = given ?? dir.firms;
  const uid = useRef(`fp${++seq}`).current;
  const [q, setQ] = useState("");
  const [typeChip, setTypeChip] = useState<TypeChip>("");
  const [showInactive, setShowInactive] = useState(false);
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const pendingKey = useRef<string | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate());
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceType[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [live, setLive] = useState("");
  const liveTimer = useRef<number | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const canCreate = allowCreate && has("firms:create");

  useEffect(() => {
    if (!open) return;
    setQ(""); setTypeChip(""); setShowInactive(false); setCursorKey(null); setCreateOpen(false); setForm(emptyCreate()); setErr(""); setLive("");
    setRecent(multi ? [] : readRecent());
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, multi]);
  useEffect(() => {
    if (!createOpen || invoiceTypes.length) return;
    invoiceTypeService.getInvoiceTypes().then(setInvoiceTypes).catch(() => setInvoiceTypes([]));
  }, [createOpen, invoiceTypes.length]);
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);

  const byId = useMemo(() => new Map(source.map((f) => [f.firmId, f])), [source]);
  const inactiveCount = useMemo(() => source.filter((f) => f.isActive === false).length, [source]);
  const filtered = useMemo(() => source.filter((f) => {
    if (!showInactive && f.isActive === false) return false;
    if (typeChip === "tuzel" && f.firmType === FirmType.GercekKisi) return false;
    if (typeChip === "sahis" && f.firmType !== FirmType.GercekKisi) return false;
    return matches(f, q.trim());
  }).sort(sortTr), [source, showInactive, typeChip, q]);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (!q.trim() && !typeChip) {
      if (multi) {
        // Kaynaktan (süzülmemiş) alınır: pasif ama seçili firma "Pasifleri göster" kapalıyken de görünsün.
        const pinned = (pinnedIds ?? []).map((id) => byId.get(id)).filter((f): f is Firm => !!f).sort(sortTr);
        if (pinned.length && selectedIds.size > 0) {
          out.push({ kind: "head", key: "h-sel", label: `Seçili · ${selectedIds.size}`, icon: I.check });
          pinned.forEach((f) => out.push({ kind: "firm", key: `s-${f.firmId}`, firm: f, recent: true }));
          out.push({ kind: "head", key: "h-all", label: `Tüm firmalar · ${filtered.length}`, icon: I.building });
        }
      } else {
        const inList = new Map(filtered.map((f) => [f.firmId, f]));
        const rec = recent.map((id) => inList.get(id)).filter((f): f is Firm => !!f);
        if (rec.length) {
          out.push({ kind: "head", key: "h-recent", label: "Son seçilenler", icon: I.clock });
          rec.forEach((f) => out.push({ kind: "firm", key: `r-${f.firmId}`, firm: f, recent: true }));
          out.push({ kind: "head", key: "h-all", label: `Tüm firmalar · ${filtered.length}`, icon: I.building });
        }
      }
    }
    filtered.forEach((f) => out.push({ kind: "firm", key: f.firmId, firm: f }));
    return out;
  }, [filtered, recent, q, typeChip, multi, pinnedIds, byId, selectedIds]);
  const firmRows = useMemo(() => rows.filter((r): r is Extract<Row, { kind: "firm" }> => r.kind === "firm"), [rows]);
  // İmleç satır anahtarıyla tutulur: "Seçili" grubu belirip kaybolunca satırlar kaysa da vurgu aynı satırda kalır.
  const keys = useMemo(() => [...(nullLabel ? ["null"] : []), ...firmRows.map((r) => r.key)], [firmRows, nullLabel]);
  const cursor = useMemo(() => {
    if (!cursorKey) return 0;
    let i = keys.indexOf(cursorKey);
    if (i < 0 && /^[sr]-/.test(cursorKey)) i = keys.indexOf(cursorKey.slice(2)); // grup satırı gitti → listedeki kopyası
    return Math.max(0, i);
  }, [keys, cursorKey]);
  const moveCursor = (delta: number) => setCursorKey(keys[Math.max(0, Math.min(keys.length - 1, cursor + delta))] ?? null);
  // Sorgu/süzgeç değişince imleç ilk sonuca gider; boş sorguda üst satır ("Tüm firmalar") kalır. Hızlı oluşturma hedefi öncelikli.
  useEffect(() => {
    if (pendingKey.current) { setCursorKey(pendingKey.current); pendingKey.current = null; return; }
    setCursorKey(keys[nullLabel && q.trim() ? 1 : 0] ?? keys[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, typeChip, showInactive]);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const announce = (msg: string) => {
    if (!multi) return;
    setLive(msg);
    window.clearTimeout(liveTimer.current);
    liveTimer.current = window.setTimeout(() => setLive(""), 1500);
  };
  /** Çoklu: işaretledikten sonra odak aramaya döner; sorgu varsa seçili kalır ki bir sonraki tuş yeni aramayı başlatsın. */
  const refocusSearch = () => { const el = searchRef.current; if (!el) return; el.focus(); if (el.value) el.select(); };
  const activate = useCallback((f: Firm | null) => {
    if (multi) {
      if (!f) announce("Seçim temizlendi");
      else { const add = !selectedIds.has(f.firmId); announce(`${f.firmName} ${add ? "eklendi" : "çıkarıldı"} · ${selectedIds.size + (add ? 1 : -1)} seçili`); }
    }
    onPick(f);
    if (multi) refocusSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, onPick, selectedIds]);

  const allVisibleSel = multi && filtered.length > 0 && filtered.every((f) => selectedIds.has(f.firmId));
  const showBulk = multi && !createOpen && !!(q.trim() || typeChip) && filtered.length > 0;
  const bulk = () => {
    const add = !allVisibleSel;
    const delta = filtered.filter((f) => selectedIds.has(f.firmId) !== add).length;
    onPickMany?.(filtered, add);
    announce(`${delta} firma ${add ? "eklendi" : "çıkarıldı"} · ${selectedIds.size + (add ? delta : -delta)} seçili`);
    refocusSearch();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (createOpen) setCreateOpen(false); else onClose(); return; }
    if (createOpen) return;
    if (multi && e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); e.stopPropagation(); onCommit?.(); return; }
    // Ok tuşları ve düz Enter yalnız arama kutusundan işlenir; odaklanmış bir düğmede Enter kendi işini yapar.
    if (e.target !== searchRef.current) return;
    if (e.key === "ArrowDown") { e.preventDefault(); moveCursor(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); moveCursor(-1); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (nullLabel && cursor === 0) return activate(null);
      const row = firmRows[nullLabel ? cursor - 1 : cursor];
      if (row) activate(row.firm);
    }
  };

  const setF = (p: Partial<ReturnType<typeof emptyCreate>>) => setForm((f) => ({ ...f, ...p }));
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.firmName.trim();
    if (!name) return setErr("Firma adı zorunludur.");
    const isPerson = form.firmType === FirmType.GercekKisi;
    const tax = form.taxNumber.replace(/\D/g, "");
    if (tax && !(isPerson ? tax.length === 11 : tax.length === 10)) return setErr(isPerson ? "TC kimlik 11 hane olmalı." : "Vergi no 10 hane olmalı.");
    setSaving(true); setErr("");
    try {
      const all = firmDirectory.peek() ?? source;
      const created = await firmService.createFirm({
        firmName: name, firmCode: generateFirmCode(name, all), firmType: form.firmType,
        contactPerson: form.contactPerson.trim() || undefined, phone: form.phone.trim() || undefined, email: form.email.trim() || undefined,
        taxNumber: !isPerson && tax ? tax : undefined, tckn: isPerson && tax ? tax : undefined, address: form.address.trim() || undefined,
        invoiceTypeId: form.invoiceTypeId ? parseInt(form.invoiceTypeId, 10) : undefined,
      } as any);
      firmDirectory.upsert(created);
      if (multi) {
        // Taslağa eklenir, modal açık kalır: kullanıcı eklemeye devam edebilir; sayfaya yalnız Uygula ile iner.
        const key = `s-${created.firmId}`; // "Seçili" grubundaki satırı
        if (q.trim() || typeChip) pendingKey.current = key; // sorgu sıfırlanınca imleç efekti bunu kullanır
        setCursorKey(key);
        setCreateOpen(false); setQ(""); setTypeChip(""); setForm(emptyCreate());
        onPick(created);
        announce(`${created.firmName} oluşturuldu ve seçime eklendi`);
        window.setTimeout(refocusSearch, 0);
      } else {
        onPick(created);
      }
    } catch (error: any) {
      const d = error?.response?.data;
      setErr(typeof d === "string" ? d : d?.message || d?.title || "Firma oluşturulamadı.");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const selectedOne = !multi ? source.find((f) => selectedIds.has(f.firmId)) : undefined;
  const none = selectedIds.size === 0;
  const subLine = dir.loading && !given ? "Yükleniyor…"
    : `${source.length} firma${inactiveCount ? ` · ${inactiveCount} pasif` : ""}${multi ? ` · ${selectedIds.size} seçili` : selectedOne ? ` · seçili: ${selectedOne.firmName}` : ""}`;
  let cursorIdx = nullLabel ? 1 : 0; // satırlara veri-cursor numarası dağıt

  return createPortal(
    <div className="fp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
      <div className={`fp-modal ${multi ? "multi" : ""}`} role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`} onMouseDown={(e) => e.stopPropagation()}>
        <header className="fp-head">
          <div>
            <span className="fp-kicker">Firma</span>
            <h2 id={`${uid}-t`}>{title}</h2>
            <p className="fp-sub">{subLine}</p>
          </div>
          <button type="button" className="fp-ib big" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>

        <div className="fp-tools">
          <div className="fp-search">{I.search}<input ref={searchRef} placeholder="Ad, kod, vergi no, yetkili, telefon, e-posta veya şehir" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button type="button" className="fp-ib" aria-label="Temizle" onClick={() => { setQ(""); searchRef.current?.focus(); }}>{I.close}</button>}</div>
          <div className="fp-chips">
            <button type="button" className={typeChip === "" ? "on" : ""} onClick={() => setTypeChip("")}>Tümü</button>
            <button type="button" className={typeChip === "tuzel" ? "on" : ""} onClick={() => setTypeChip("tuzel")}>Tüzel</button>
            <button type="button" className={typeChip === "sahis" ? "on" : ""} onClick={() => setTypeChip("sahis")}>Şahıs</button>
          </div>
          {inactiveCount > 0 && <label className="fp-toggle"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Pasifleri göster</label>}
          {showBulk && <button type="button" className="fp-btn" onClick={bulk}>{allVisibleSel ? `Görünenleri kaldır · ${filtered.length}` : `Görünenleri seç · ${filtered.length}`}</button>}
          {canCreate && <button type="button" className={`fp-btn ${createOpen ? "" : "primary"}`} onClick={() => { setCreateOpen((v) => !v); setErr(""); }}>{createOpen ? "Listeye dön" : <>{I.plus} Yeni firma</>}</button>}
        </div>

        {createOpen ? (
          <form className="fp-create" onSubmit={create}>
            <div className="fp-create-head"><b>Yeni firma</b><span>Kod otomatik üretilir; ayrıntılar sonra Firma Yönetimi'nden tamamlanabilir.</span></div>
            {err && <div className="fp-error">{err}</div>}
            <div className="fp-grid">
              <label className="fp-f span2"><span>Ad / unvan <em>zorunlu</em></span><input value={form.firmName} autoFocus onChange={(e) => setF({ firmName: e.target.value })} placeholder="Örn. Bossa Tekstil San. A.Ş." /></label>
              <label className="fp-f"><span>Tür</span><select value={form.firmType} onChange={(e) => setF({ firmType: Number(e.target.value) as FirmType, taxNumber: "" })}><option value={FirmType.TuzelKisi}>Tüzel kişi (şirket)</option><option value={FirmType.GercekKisi}>Gerçek kişi (şahıs)</option></select></label>
              <label className="fp-f"><span>{form.firmType === FirmType.GercekKisi ? "TC kimlik no" : "Vergi no"} <em>{form.firmType === FirmType.GercekKisi ? "11 hane" : "10 hane"}</em></span><input value={form.taxNumber} inputMode="numeric" maxLength={11} onChange={(e) => setF({ taxNumber: e.target.value.replace(/\D/g, "") })} /></label>
              <label className="fp-f"><span>Yetkili kişi</span><input value={form.contactPerson} onChange={(e) => setF({ contactPerson: e.target.value })} /></label>
              <label className="fp-f"><span>Telefon</span><input value={form.phone} onChange={(e) => setF({ phone: e.target.value })} placeholder="0212 000 00 00" /></label>
              <label className="fp-f"><span>E-posta</span><input type="email" value={form.email} onChange={(e) => setF({ email: e.target.value })} /></label>
              <label className="fp-f"><span>Fatura tipi</span><select value={form.invoiceTypeId} onChange={(e) => setF({ invoiceTypeId: e.target.value })}><option value="">Seçilmedi</option>{invoiceTypes.map((t) => <option key={t.invoiceTypeId} value={t.invoiceTypeId}>{t.name}</option>)}</select></label>
              <label className="fp-f span2"><span>Adres</span><input value={form.address} onChange={(e) => setF({ address: e.target.value })} /></label>
            </div>
            <div className="fp-create-foot"><button type="button" className="fp-btn" onClick={() => setCreateOpen(false)} disabled={saving}>Vazgeç</button><button type="submit" className="fp-btn primary" disabled={saving}>{saving ? "Oluşturuluyor…" : multi ? "Oluştur ve ekle" : "Oluştur ve seç"}</button></div>
          </form>
        ) : (
          <div className="fp-list" ref={listRef} role="listbox" id={`${uid}-l`} aria-label="Firmalar" aria-multiselectable={multi || undefined}>
            {dir.error && !given && <div className="fp-blank"><b>{dir.error}</b><button type="button" className="fp-btn" onClick={() => dir.reload(true)}>Yeniden dene</button></div>}
            {nullLabel && (
              <button type="button" role="option" aria-selected={none} tabIndex={-1} id={`${uid}-0`} className={`fp-row null ${cursor === 0 ? "cur" : ""} ${none ? "sel" : ""}`} data-cursor={0} onMouseEnter={() => setCursorKey("null")} onClick={() => activate(null)}>
                <span className="fp-mark null">{I.building}</span>
                <span className="fp-text"><b>{nullLabel}</b><small>süzgeç uygulanmaz</small></span>
                {multi ? <span className={`fp-check ${none ? "" : "empty"}`} aria-hidden="true">{I.check}</span> : none && <span className="fp-check">{I.check}</span>}
              </button>
            )}
            {rows.length === 0 && !dir.loading && !dir.error && (
              <div className="fp-blank"><span className="fp-blank-ico">{I.inbox}</span><b>{q ? `"${q}" için firma bulunamadı` : "Firma yok"}</b>{q ? "Aramayı değiştirin ya da yeni firma oluşturun." : "Firma Yönetimi'nden ekleyin."}{canCreate && <button type="button" className="fp-btn primary" onClick={() => { setCreateOpen(true); setF({ firmName: q }); }}>{I.plus} "{q || "Yeni firma"}" oluştur</button>}</div>
            )}
            {rows.map((r) => {
              if (r.kind === "head") return <div key={r.key} className="fp-group">{r.icon}{r.label}</div>;
              const f = r.firm; const idx = cursorIdx++;
              const checked = selectedIds.has(f.firmId);
              return (
                <button type="button" key={r.key} role="option" aria-selected={checked} tabIndex={-1} id={`${uid}-${idx}`} className={`fp-row ${cursor === idx ? "cur" : ""} ${checked ? "sel" : ""} ${f.isActive === false ? "off" : ""}`} data-cursor={idx} onMouseEnter={() => setCursorKey(r.key)} onClick={() => activate(f)}>
                  <FirmMark firm={f} />
                  <span className="fp-text">
                    <b>{f.firmName}{f.firmCode && <span className="fp-code">{f.firmCode}</span>}{f.isActive === false && <span className="fp-badge off">Pasif</span>}</b>
                    <small>{subText(f) || "—"}</small>
                  </span>
                  <span className="fp-side hide-sm">{f.contactPerson && <b>{f.contactPerson}</b>}{(f.phone || f.email) && <small>{f.phone || f.email}</small>}</span>
                  {multi ? <span className={`fp-check ${checked ? "" : "empty"}`} aria-hidden="true">{I.check}</span> : checked && <span className="fp-check">{I.check}</span>}
                </button>
              );
            })}
            {rows.length > 0 && q && filtered.length !== source.length && <div className="fp-count">{filtered.length} / {source.length} firma</div>}
          </div>
        )}

        <footer className="fp-foot">
          {multi
            ? <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> işaretle · <kbd>Ctrl</kbd>+<kbd>Enter</kbd> uygula · <kbd>Esc</kbd> vazgeç</span>
            : <span className="fp-keys"><kbd>↑</kbd><kbd>↓</kbd> gezin · <kbd>Enter</kbd> seç · <kbd>Esc</kbd> kapat</span>}
          {multi ? (!createOpen && (
            <span className="fp-actions">
              {allowClear && !none && <button type="button" className="fp-btn danger" onClick={() => activate(null)}>Temizle</button>}
              <button type="button" className="fp-btn" onClick={onClose}>Vazgeç</button>
              <button type="button" className="fp-btn primary" onClick={onCommit}>{none ? "Uygula" : `Uygula · ${selectedIds.size}`}</button>
            </span>
          )) : (allowClear && !none && !createOpen && <button type="button" className="fp-btn danger" onClick={() => onPick(null)}>Seçimi kaldır</button>)}
        </footer>
        {multi && <div className="fp-sr" role="status" aria-live="polite" aria-atomic="true">{live}</div>}
      </div>
    </div>,
    document.body,
  );
};

// ───────────────────────────── Tekli modal ─────────────────────────────

export interface FirmPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Seçim; `nullLabel` satırı seçilirse ya da "Seçimi kaldır" ile null gelir. */
  onSelect: (firm: Firm | null) => void;
  selectedFirmId?: string | null;
  /** Verilirse yalnız bu liste gösterilir (ör. yalnız modeli olan firmalar). */
  firms?: Firm[];
  title?: string;
  /** Süzgeç bağlamı için üst satır: "Tüm firmalar" gibi. */
  nullLabel?: string;
  /** Satır içi hızlı firma oluşturma (ayrıca firms:create izni gerekir). */
  allowCreate?: boolean;
  /** Seçili firma varken "Seçimi kaldır" düğmesi. */
  allowClear?: boolean;
}

export const FirmPickerModal: React.FC<FirmPickerModalProps> = ({ open, onClose, onSelect, selectedFirmId, firms, title = "Firma seç", nullLabel, allowCreate = true, allowClear = true }) => {
  const ids = useMemo(() => new Set(selectedFirmId ? [selectedFirmId] : []), [selectedFirmId]);
  const pick = useCallback((f: Firm | null) => { if (f) pushRecent(f.firmId); onSelect(f); onClose(); }, [onSelect, onClose]);
  return <PickerBase mode="single" open={open} onClose={onClose} selectedIds={ids} onPick={pick} firms={firms} title={title} nullLabel={nullLabel} allowCreate={allowCreate} allowClear={allowClear} />;
};

// ───────────────────────────── Çoklu modal ─────────────────────────────

export interface FirmMultiPickerModalProps {
  open: boolean;
  /** Vazgeç / Esc / X / overlay: taslak atılır, onApply çağrılmaz. */
  onClose: () => void;
  /** Kesinleşmiş seçim; her açılışta taslak bundan kopyalanır. */
  value: string[];
  /**
   * "Uygula" / Ctrl+Enter — yalnız küme değiştiyse çağrılır. `firmIds` taslak sırasıdır (önce value'dakiler,
   * sonra işaretleme sırası); dizinde bulunmayan (silinmiş) kimlikler firmIds'te kalır, `firms` içinde yer almaz.
   */
  onApply: (firmIds: string[], firms: Firm[]) => void;
  /** Verilirse yalnız bu liste gösterilir. */
  firms?: Firm[];
  title?: string;
  /** Üst satır ("Tüm firmalar"): taslak boşken işaretli görünür, tıklanınca taslağı boşaltır. */
  nullLabel?: string;
  /** Satır içi hızlı oluşturma; oluşturulan firma taslağa eklenir, modal kapanmaz. */
  allowCreate?: boolean;
  /** Alt çubukta "Temizle". */
  allowClear?: boolean;
}

export const FirmMultiPickerModal: React.FC<FirmMultiPickerModalProps> = ({ open, onClose, value, onApply, firms, title = "Firmaları seç", nullLabel, allowCreate = true, allowClear = true }) => {
  const [draft, setDraft] = useState<Set<string>>(() => new Set());
  const [pinned, setPinned] = useState<string[]>([]);
  const dir = useFirmDirectory(open && !firms);
  const source = firms ?? dir.firms;
  // Taslak yalnız açılışta tohumlanır; kapalıyken value değişse de dokunulmaz.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setDraft(new Set(value)); setPinned([...value]); } }, [open]);
  const pin = (ids: string[]) => setPinned((p) => [...p, ...ids.filter((id) => !p.includes(id))]);
  const toggle = useCallback((f: Firm | null) => {
    if (!f) { setDraft(new Set()); setPinned([]); return; }
    setDraft((d) => { const n = new Set(d); if (n.has(f.firmId)) n.delete(f.firmId); else n.add(f.firmId); return n; });
    pin([f.firmId]);
  }, []);
  const pickMany = (fs: Firm[], add: boolean) => {
    setDraft((d) => { const n = new Set(d); fs.forEach((f) => { if (add) n.add(f.firmId); else n.delete(f.firmId); }); return n; });
    if (add) pin(fs.map((f) => f.firmId));
  };
  const commit = () => {
    const ids = Array.from(draft);
    if (!sameSet(ids, value)) onApply(ids, ids.map((id) => source.find((f) => f.firmId === id)).filter((f): f is Firm => !!f));
    onClose();
  };
  return <PickerBase mode="multi" open={open} onClose={onClose} selectedIds={draft} onPick={toggle} onPickMany={pickMany} onCommit={commit} pinnedIds={pinned} firms={firms} title={title} nullLabel={nullLabel} allowCreate={allowCreate} allowClear={allowClear} />;
};

// ───────────────────────────── Tekli alan ─────────────────────────────

export interface FirmPickerFieldProps {
  value?: string | null;
  /** Çözümlenmiş firma nesnesi verilirse dizin beklenmez. */
  firm?: Firm | null;
  onChange: (firm: Firm | null) => void;
  placeholder?: string;
  /** Süzgeç bağlamı: boş değer bu etiketle gösterilir ve modalde üst satır olur. */
  nullLabel?: string;
  disabled?: boolean;
  allowClear?: boolean;
  allowCreate?: boolean;
  firms?: Firm[];
  title?: string;
  /** `toolbar`: araç çubuğu genişliği (12–18rem); `form`: tam genişlik. */
  variant?: "form" | "toolbar";
  className?: string;
  id?: string;
  autoFocus?: boolean;
}

export const FirmPickerField: React.FC<FirmPickerFieldProps> = ({ value, firm: givenFirm, onChange, placeholder = "Firma seçin", nullLabel, disabled, allowClear = true, allowCreate = true, firms, title, variant = "form", className = "", id, autoFocus }) => {
  const [open, setOpen] = useState(false);
  const ctl = useRef<HTMLButtonElement>(null);
  // Verilen liste (ör. yalnız modeli olan firmalar) seçili kaydı içermeyebilir; dizine düşülür.
  const needDir = !!value && !givenFirm && !(firms && firms.some((f) => f.firmId === value));
  const dir = useFirmDirectory(needDir);
  const firm = givenFirm ?? (value ? (firms?.find((f) => f.firmId === value) ?? dir.firms.find((f) => f.firmId === value)) : undefined) ?? null;
  const showClear = allowClear && !!value && !disabled;
  const close = () => { setOpen(false); window.setTimeout(() => ctl.current?.focus(), 0); };
  return (
    <div className={`fp-field ${variant} ${disabled ? "disabled" : ""} ${firm ? "has" : ""} ${className}`}>
      <button ref={ctl} type="button" id={id} className="fp-control" disabled={disabled} autoFocus={autoFocus} aria-haspopup="dialog" aria-expanded={open} title={firm ? `${firm.firmName}${firm.firmCode ? ` · ${firm.firmCode}` : ""}` : undefined} onClick={() => setOpen(true)}>
        {firm ? <FirmMark firm={firm} /> : <span className="fp-mark null">{I.building}</span>}
        <span className="fp-text">
          {firm ? <><b>{firm.firmName}</b><small>{fieldSub(firm)}</small></>
            : value && needDir && dir.loading ? <b className="fp-placeholder">Yükleniyor…</b>
            : <b className="fp-placeholder">{nullLabel && !value ? nullLabel : placeholder}</b>}
        </span>
        <span className="fp-chev">{I.chev}</span>
      </button>
      {showClear && <button type="button" className="fp-clear" aria-label="Seçimi kaldır" title="Seçimi kaldır" onClick={() => onChange(null)}>{I.close}</button>}
      <FirmPickerModal open={open} onClose={close} onSelect={onChange} selectedFirmId={value} firms={firms} title={title} nullLabel={nullLabel} allowCreate={allowCreate} allowClear={allowClear} />
    </div>
  );
};

// ───────────────────────────── Çoklu alan ─────────────────────────────

export interface FirmMultiPickerFieldProps {
  value: string[];
  /** Modalin Uygula'sından (yalnız değişince) ve alan üstündeki × ile ([], []) olarak gelir. */
  onChange: (firmIds: string[], firms: Firm[]) => void;
  placeholder?: string;
  /** Boş dizi bu etiketle gösterilir ve modalde üst satır olur. */
  nullLabel?: string;
  disabled?: boolean;
  allowClear?: boolean;
  allowCreate?: boolean;
  firms?: Firm[];
  title?: string;
  variant?: "form" | "toolbar";
  className?: string;
  id?: string;
  autoFocus?: boolean;
}

export const FirmMultiPickerField: React.FC<FirmMultiPickerFieldProps> = ({ value, onChange, placeholder = "Firma seçin", nullLabel, disabled, allowClear = true, allowCreate = true, firms, title, variant = "form", className = "", id, autoFocus }) => {
  const [open, setOpen] = useState(false);
  const ctl = useRef<HTMLButtonElement>(null);
  const n = value.length;
  const needDir = n > 0 && !(firms && value.every((id) => firms.some((f) => f.firmId === id)));
  const dir = useFirmDirectory(needDir);
  const resolved = value.map((id) => firms?.find((f) => f.firmId === id) ?? dir.firms.find((f) => f.firmId === id)).filter((f): f is Firm => !!f);
  const names = resolved.map((f) => f.firmName);
  const showClear = allowClear && n > 0 && !disabled;
  const close = () => { setOpen(false); window.setTimeout(() => ctl.current?.focus(), 0); };
  const one = n === 1 ? resolved[0] : undefined;
  return (
    <div className={`fp-field ${variant} ${disabled ? "disabled" : ""} ${n > 0 ? "has" : ""} ${className}`}>
      <button ref={ctl} type="button" id={id} className="fp-control" disabled={disabled} autoFocus={autoFocus} aria-haspopup="dialog" aria-expanded={open} title={names.length ? names.join(", ") : undefined} aria-label={n >= 2 ? `Firmalar: ${names.join(", ")} (${n} seçili)` : undefined} onClick={() => setOpen(true)}>
        {n === 0 ? <span className="fp-mark null">{I.building}</span>
          : one ? <FirmMark firm={one} />
          : n === 1 ? <span className="fp-mark null">{I.building}</span>
          : <span className="fp-mark count">{n > 9 ? "9+" : n}</span>}
        <span className="fp-text">
          {n === 0 ? <b className="fp-placeholder">{nullLabel ?? placeholder}</b>
            : resolved.length === 0 && needDir && dir.loading ? <b className="fp-placeholder">Yükleniyor…</b>
            : one ? <><b>{one.firmName}</b><small>{fieldSub(one)}</small></>
            : n === 1 ? <b>Bilinmeyen firma</b>
            : <><b><span className="fp-name">{names[0] ?? "Bilinmeyen firma"}</span><span className="fp-more">+{n - 1}</span></b><small>{names.slice(1).join(" · ") || `${n} firma`}</small></>}
        </span>
        <span className="fp-chev">{I.chev}</span>
      </button>
      {showClear && <button type="button" className="fp-clear" aria-label="Seçimi temizle" title="Seçimi temizle" onClick={() => onChange([], [])}>{I.close}</button>}
      <FirmMultiPickerModal open={open} onClose={close} value={value} onApply={onChange} firms={firms} title={title} nullLabel={nullLabel} allowCreate={allowCreate} allowClear={allowClear} />
    </div>
  );
};

export default FirmPickerField;
